
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PartnerRecruitPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    experience: '',
    area: '',
    motivation: '',
    availability: '',
    agreement: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agreement) {
      setSubmitError('活動方針への同意が必要です');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d64l5qar6bioa6qf6i00',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: new URLSearchParams({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            experience: formData.experience,
            area: formData.area,
            motivation: formData.motivation,
            availability: formData.availability,
            agreement: formData.agreement ? 'はい' : 'いいえ'
          }).toString()
        }
      );

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          role: '',
          experience: '',
          area: '',
          motivation: '',
          availability: '',
          agreement: false
        });
      } else {
        setSubmitError('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const impactStats = [
    { number: '76', label: '利用中のご家族', icon: 'ri-group-line' },
    { number: '11', label: '活動中のパートナー', icon: 'ri-user-star-line' },
    { number: '320+', label: '寄り添った相談', icon: 'ri-chat-heart-line' },
    { number: '98%', label: '満足度', icon: 'ri-heart-3-line' }
  ];

  const roles = [
    {
      value: '看護師',
      label: '看護師',
      icon: 'ri-nurse-line',
      color: 'teal'
    },
    { value: '保育士', label: '保育士', icon: 'ri-parent-line', color: 'amber' },
    {
      value: '先輩ママ',
      label: '先輩ママ（医療的ケア児の保護者経験者）',
      icon: 'ri-heart-line',
      color: 'rose'
    },
    {
      value: '理学療法士',
      label: '理学療法士',
      icon: 'ri-run-line',
      color: 'indigo'
    },
    {
      value: '社会福祉士',
      label: '社会福祉士',
      icon: 'ri-hand-heart-line',
      color: 'orange'
    },
    {
      value: 'その他',
      label: 'その他の専門職',
      icon: 'ri-user-line',
      color: 'gray'
    }
  ];

  const areas = [
    '仙台市',
    '石巻市',
    '名取市',
    '多賀城市',
    '大崎市',
    '塩竈市',
    '登米市',
    '気仙沼市',
    '栗原市',
    '東松島市',
    'その他宮城県内'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ナビゲーション */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
              <i className="ri-ship-line text-white text-lg sm:text-xl"></i>
            </div>
            <span className="text-base sm:text-xl font-bold text-gray-900">
              みやぎスマイル・ポート
            </span>
          </Link>
          {/* デスクトップメニュー */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              ホーム
            </Link>
            <Link
              to="/partners"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              パートナー一覧
            </Link>
          </div>
          {/* モバイルメニューボタン */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i
              className={`${
                mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
              } text-2xl`}
            ></i>
          </button>
        </div>
        {/* モバイルメニュー */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                ホーム
              </Link>
              <Link
                to="/partners"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
              >
                パートナー一覧
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* ヒーローセクション */}
      <section className="relative pt-20 sm:pt-24 pb-12 sm:pb-20 bg-gradient-to-br from-purple-50 via-rose-50 to-teal-50 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-40 sm:w-64 h-40 sm:h-64 bg-purple-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-52 sm:w-80 h-52 sm:h-80 bg-teal-200 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6 whitespace-nowrap text-sm">
              <i className="ri-user-star-line"></i>
              <span className="font-semibold">Smile Partner 募集</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              あなたの経験と専門性で
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-purple-600">
                家族の笑顔を支えませんか？
              </span>
            </h1>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              医療的ケア児を育てる家族は、孤立しがちです。
              <br className="hidden sm:block" />
              あなたの「共感」と「寄り添い」が、誰かの心を軽くします。
            </p>
          </div>

          {/* インパクト統計 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto">
            {impactStats.map((stat, index) => (
              <div
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-lg"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <i className={`${stat.icon} text-teal-600 text-lg sm:text-xl`}></i>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-0.5 sm:mb-1">
                  {stat.number}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* なぜパートナーが必要か */}
      <section className="py-12 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              なぜスマイル・パートナーが
              <br />
              社会に必要なのか
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-20">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/6bef9d00c7c0134664fb306f8c57d02c.jpg"
                alt="孤立する家族"
                className="w-full h-56 sm:h-80 object-cover object-top rounded-2xl sm:rounded-3xl shadow-xl"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6 whitespace-nowrap text-sm">
                <i className="ri-alert-line"></i>
                <span className="font-semibold">現状の課題</span>
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                医療的ケア児の家族が
                <br />
                直面する孤立
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3 sm:gap-4 bg-gray-50 rounded-xl p-3 sm:p-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-unfollow-line text-rose-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                      相談できる人がいない
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      同じ経験を持つ人が周囲におらず、悩みを共有できない
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4 bg-gray-50 rounded-xl p-3 sm:p-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-emotion-sad-line text-amber-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                      理解されない辛さ
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      「頑張って」という言葉が逆に重荷になることも
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4 bg-gray-50 rounded-xl p-3 sm:p-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mental-health-line text-purple-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                      心身の疲弊
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      24時間のケアで休む暇がなく、心が限界に
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6 whitespace-nowrap text-sm">
                <i className="ri-lightbulb-line"></i>
                <span className="font-semibold">パートナーの役割</span>
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                あなたの存在が
                <br />
                希望の光になる
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3 sm:gap-4 bg-gradient-to-r from-teal-50 to-transparent rounded-xl p-3 sm:p-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-3-line text-teal-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                      共感で心を軽くする
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      「わかるよ」の一言が、どれだけ救いになるか
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4 bg-gradient-to-r from-purple-50 to-transparent rounded-xl p-3 sm:p-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-star-line text-purple-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                      専門知識で安心を提供
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      医療・福祉の知識が、不安を和らげます
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4 bg-gradient-to-r from-rose-50 to-transparent rounded-xl p-3 sm:p-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-links-line text-rose-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                      孤立を防ぐ架け橋
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      一人じゃないと感じられる繋がりを作る
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20supportive%20hand%20reaching%20out%20to%20help%20another%20person%20gentle%20pastel%20colors%20of%20teal%20and%20rose%20soft%20lighting%20hopeful%20atmosphere%20artistic%20illustration%20style%20showing%20connection%20and%20support&width=600&height=450&seq=support1&orientation=landscape"
                alt="支え合い"
                className="w-full h-56 sm:h-80 object-cover object-top rounded-2xl sm:rounded-3xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* パートナーの声 */}
      <section className="py-12 sm:py-24 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              活動中のパートナーの声
            </h2>
            <p className="text-base sm:text-xl text-gray-600">
              実際に活動しているパートナーからのメッセージ
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20female%20nurse%20in%20her%2040s%20with%20warm%20caring%20smile%20soft%20natural%20lighting%20clean%20white%20background%20portrait%20style&width=200&height=200&seq=partner-voice1&orientation=squarish"
                  alt="看護師パートナー"
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover object-top border-4 border-teal-100"
                />
                <div>
                  <h4 className="font-bold text-gray-900 text-sm sm:text-base">
                    山田 美和さん
                  </h4>
                  <p className="text-xs sm:text-sm text-teal-600">
                    看護師・仙台市
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                「病院では伝えきれない、日常のケアの不安に寄り添えることにやりがいを感じています。『話を聞いてもらえて楽になった』という言葉が何より嬉しいです。」
              </p>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                <i className="ri-time-line"></i>
                <span>活動歴 1年6ヶ月</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20Japanese%20mother%20in%20her%2030s%20with%20gentle%20understanding%20expression%20soft%20natural%20lighting%20clean%20white%20background%20portrait%20style&width=200&height=200&seq=partner-voice2&orientation=squarish"
                  alt="先輩ママパートナー"
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover object-top border-4 border-rose-100"
                />
                <div>
                  <h4 className="font-bold text-gray-900 text-sm sm:text-base">
                    佐々木 恵さん
                  </h4>
                  <p className="text-xs sm:text-sm text-rose-600">
                    先輩ママ・名取市
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                「自分が一番辛かった時期を思い出しながら活動しています。あの時、こんな場所があったら…という思いが原動力です。」
              </p>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                <i className="ri-time-line"></i>
                <span>活動歴 8ヶ月</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20male%20social%20worker%20in%20his%2030s%20with%20kind%20supportive%20expression%20soft%20natural%20lighting%20clean%20white%20background%20portrait%20style&width=200&height=200&seq=partner-voice3&orientation=squarish"
                  alt="社会福祉士パートナー"
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover object-top border-4 border-orange-100"
                />
                <div>
                  <h4 className="font-bold text-gray-900 text-sm sm:text-base">
                    高橋 健一さん
                  </h4>
                  <p className="text-xs sm:text-sm text-orange-600">
                    社会福祉士・石巻市
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                「制度の情報を伝えるだけでなく、まず気持ちを受け止めることを大切にしています。オンラインだからこそ、気軽に相談してもらえる良さがあります。」
              </p>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                <i className="ri-time-line"></i>
                <span>活動歴 1年</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 活動内容 */}
      <section className="py-12 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              パートナーの活動内容
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8 mb-10 sm:mb-16">
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-teal-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-teal-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-chat-heart-line text-teal-600 text-xl sm:text-2xl"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                掲示板での返信
              </h3>
              <p className="text-gray-600 leading-relaxed mb-3 sm:mb-4 text-sm">
                匿名掲示板に投稿された悩みに、共感と寄り添いの返信をします。アドバイスよりも「気持ちを受け止める」ことを大切に。
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-teal-500"></i>
                  <span>週に数回、空いた時間で</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-teal-500"></i>
                  <span>スマホからでも対応可能</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-purple-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-book-open-line text-purple-600 text-xl sm:text-2xl"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                マニュアル作成協力
              </h3>
              <p className="text-gray-600 leading-relaxed mb-3 sm:mb-4 text-sm">
                専門知識や経験を活かして、お守りマニュアルの内容充実に協力いただきます。
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-purple-500"></i>
                  <span>得意分野での執筆・監修</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-purple-500"></i>
                  <span>任意参加</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-rose-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-rose-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-team-line text-rose-600 text-xl sm:text-2xl"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                パートナー交流会
              </h3>
              <p className="text-gray-600 leading-relaxed mb-3 sm:mb-4 text-sm">
                月1回のオンライン交流会で、他のパートナーと情報交換や悩み共有ができます。
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-rose-500"></i>
                  <span>オンライン開催</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-rose-500"></i>
                  <span>参加は任意</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center">
              活動条件
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-md">
                  <i className="ri-time-line text-teal-600 text-lg sm:text-xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                  活動時間
                </h4>
                <p className="text-xs sm:text-sm text-gray-600">
                  週1〜2時間程度<br />
                  ご自身のペースで
                </p>
              </div>
              <div className="text-center">
                <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-md">
                  <i className="ri-money-yen-circle-line text-teal-600 text-lg sm:text-xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                  報酬
                </h4>
                <p className="text-xs sm:text-sm text-gray-600">
                  ボランティア活動<br />
                  （交通費等は応相談）
                </p>
              </div>
              <div className="text-center">
                <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-md">
                  <i className="ri-map-pin-line text-teal-600 text-lg sm:text-xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                  活動場所
                </h4>
                <p className="text-xs sm:text-sm text-gray-600">
                  完全オンライン<br />
                  自宅から参加可能
                </p>
              </div>
              <div className="text-center">
                <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-md">
                  <i className="ri-graduation-cap-line text-teal-600 text-lg sm:text-xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">
                  研修
                </h4>
                <p className="text-xs sm:text-sm text-gray-600">
                  活動前に<br />
                  オンライン研修あり
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 登録フォーム */}
      <section id="form" className="py-12 sm:py-24 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              パートナー登録フォーム
            </h2>
            <p className="text-sm sm:text-lg text-gray-600">
              ご興味をお持ちいただきありがとうございます。
              <br />
              以下のフォームからお申し込みください。
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 shadow-xl text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-check-line text-teal-600 text-3xl sm:text-4xl"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                ご登録ありがとうございます！
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                担当者より3営業日以内にご連絡いたします。<br />
                しばらくお待ちください。
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                ホームに戻る
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ) : (
            <form
              id="partner-registration-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-12 shadow-xl"
            >
              <div className="space-y-5 sm:space-y-6">
                {/* お名前 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    お名前 <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                {/* メールアドレス */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    メールアドレス <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                {/* 電話番号 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                {/* 希望する役割 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    希望する役割 <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 経験・資格 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    関連する経験・資格 <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                    placeholder="例：看護師として10年勤務、小児科経験あり / 医療的ケア児の母として5年の経験"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.experience.length}/500文字
                  </p>
                </div>

                {/* 活動エリア */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    お住まいのエリア <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="area"
                    value={formData.area}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    {areas.map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 志望動機 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    志望動機 <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                    placeholder="パートナーとして活動したい理由をお聞かせください"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.motivation.length}/500文字
                  </p>
                </div>

                {/* 活動可能な時間帯 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    活動可能な時間帯
                  </label>
                  <input
                    type="text"
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="例：平日夜、週末午前中など"
                  />
                </div>

                {/* 活動方針への同意 */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                  <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">
                    活動方針への同意
                  </h4>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4">
                    <p className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>
                        共感を最優先し、「正論」ではなく「その気持ち、わかるよ」を大切にします
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>いきなりのアドバイスや安易な励ましを避けます</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>利用者のプライバシーを厳守します</span>
                    </p>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="agreement"
                      checked={formData.agreement}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                      上記の活動方針に同意します{' '}
                      <span className="text-rose-500">*</span>
                    </span>
                  </label>
                </div>

                {submitError && (
                  <div className="bg-rose-50 text-rose-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white py-3 sm:py-4 rounded-xl font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      送信中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      登録を申し込む
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-ship-line text-white text-lg sm:text-xl"></i>
              </div>
              <span className="text-lg sm:text-xl font-bold">
                みやぎスマイル・ポート
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-teal-100 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Powered by Readdy
              </a>
              <p className="text-xs sm:text-sm text-teal-100">
                &copy; 2025 スマイル・ポート. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
